import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Search } from 'lucide-react';
import api from '../services/api';
import Layout from '../components/Layout';
import { AlunosByResponsavel } from '../components/AlunosByResponsavel';
import styles from './ResponsaveisPage.module.css';

interface Responsavel {
    id: number;
    nome: string;
    cpfCnpj: string;
    rg?: string;
    dataNascimento?: string;
    nacionalidade?: string;
    estadoCivil?: string;
    profissao?: string;
    grauInstrucao?: string;
    localTrabalho?: string;
    rendaMensal?: number;
    declaraIR?: boolean;
    email: string;
    telefone: string;
    telefoneResidencial?: string;
    telefoneComercial?: string;
    endereco: {
        id?: number;
        endereco: string;
        numero?: string;
        complemento?: string;
        bairro?: string;
        cidade: string;
        estado: string;
        cep: string;
    };
    tipoPessoa: number;
    ativo: boolean;
}

const INITIAL_FORM_STATE = {
    nome: '',
    cpfCnpj: '',
    rg: '',
    dataNascimento: '',
    nacionalidade: 'Brasileiro(a)',
    estadoCivil: 'Solteiro(a)',
    profissao: '',
    grauInstrucao: '',
    localTrabalho: '',
    rendaMensal: 0,
    declaraIR: false,
    email: '',
    telefone: '',
    telefoneResidencial: '',
    telefoneComercial: '',
    endereco: {
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
    },
    tipoPessoa: 1,
};

const ResponsaveisPage: React.FC = () => {
    const [responsaveis, setResponsaveis] = useState<Responsavel[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 30;

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState<any>(INITIAL_FORM_STATE);
    const [editingId, setEditingId] = useState<number | null>(null);

    useEffect(() => {
        fetchResponsaveis();
    }, []);

    const fetchResponsaveis = async () => {
        try {
            setLoading(true);
            const res = await api.get('/responsaveis');
            setResponsaveis(res.data);
        } catch (err) {
            console.error('Erro ao buscar responsáveis', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (responsavel?: Responsavel) => {
        if (responsavel) {
            setFormData({
                ...responsavel,
                dataNascimento: responsavel.dataNascimento ? responsavel.dataNascimento.split('T')[0] : '',
                endereco: responsavel.endereco ? { ...responsavel.endereco } : { ...INITIAL_FORM_STATE.endereco }
            });
            setEditingId(responsavel.id);
        } else {
            setFormData(INITIAL_FORM_STATE);
            setEditingId(null);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormData(INITIAL_FORM_STATE);
        setEditingId(null);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        let finalValue: any = value;

        if (type === 'checkbox') {
            finalValue = (e.target as HTMLInputElement).checked;
        } else if (type === 'number') {
            finalValue = value ? parseFloat(value) : 0;
        }

        setFormData((prev: any) => ({ ...prev, [name]: finalValue }));
    };

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            endereco: {
                ...prev.endereco,
                [name]: value
            }
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.patch(`/responsaveis/${editingId}`, formData);
            } else {
                await api.post('/responsaveis', formData);
            }
            handleCloseModal();
            fetchResponsaveis();
        } catch (error) {
            console.error('Erro ao salvar responsável', error);
            alert('Erro ao salvar. Verifique os dados e tente novamente.');
        }
    };

    const filteredResponsaveis = responsaveis.filter(r =>
        r.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.cpfCnpj.includes(searchTerm)
    );

    const totalPages = Math.ceil(filteredResponsaveis.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentResponsaveis = filteredResponsaveis.slice(startIndex, startIndex + itemsPerPage);

    return (
        <Layout title="Responsáveis">
            <div className={styles.pageContainer}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <Users size={32} className={styles.headerIcon} />
                        <div>
                            <h1>Responsáveis Financeiros</h1>
                            <p>Cadastre e gerencie os responsáveis pelos alunos</p>
                        </div>
                    </div>
                    <button className={styles.addButton} onClick={() => handleOpenModal()}>
                        <Plus size={20} />
                        Novo Responsável
                    </button>
                </div>

                {/* Filters */}
                <div className={styles.filtersSection}>
                    <div className={styles.searchBox}>
                        <Search size={20} className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Buscar por nome ou CPF/CNPJ..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Table */}
                <div className={styles.tableCard}>
                    {loading ? (
                        <div className={styles.loadingState}>Carregando responsáveis...</div>
                    ) : filteredResponsaveis.length === 0 ? (
                        <div className={styles.emptyState}>
                            <Users size={48} className={styles.emptyIcon} />
                            <h3>Nenhum responsável encontrado</h3>
                            <p>Comece adicionando um novo responsável financeiro.</p>
                        </div>
                    ) : (
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>CPF/CNPJ</th>
                                        <th>Contato</th>
                                        <th>Cidade/UF</th>
                                        <th>Status</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentResponsaveis.map(resp => (
                                        <tr
                                            key={resp.id}
                                            className={styles.tableRow}
                                            onClick={() => handleOpenModal(resp)}
                                            title="Clique para editar"
                                        >
                                            <td>
                                                <div className={styles.tdMainText}>{resp.nome}</div>
                                                <div className={styles.tdSubText}>{resp.email}</div>
                                            </td>
                                            <td>{resp.cpfCnpj}</td>
                                            <td>{resp.telefone}</td>
                                            <td>{resp.endereco?.cidade} - {resp.endereco?.estado}</td>
                                            <td>
                                                <span className={resp.ativo ? styles.badgeActive : styles.badgeInactive}>
                                                    {resp.ativo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className={styles.actionButtons}>
                                                    <button
                                                        className={styles.deleteBtn}
                                                        title="Excluir"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            if (window.confirm(`Deseja excluir ${resp.nome}?`)) {
                                                                try {
                                                                    await api.delete(`/responsaveis/${resp.id}`);
                                                                    setResponsaveis(responsaveis.filter(r => r.id !== resp.id));
                                                                } catch (err) {
                                                                    console.error('Erro ao deletar', err);
                                                                    alert('Erro ao deletar responsável');
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && filteredResponsaveis.length > 0 && totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className={styles.pageButton}
                            >
                                Anterior
                            </button>
                            <span className={styles.pageInfo}>
                                Página {currentPage} de {totalPages}
                            </span>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className={styles.pageButton}
                            >
                                Próxima
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal / Drawer Form */}
            {isModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h2>{editingId ? 'Editar Responsável' : 'Novo Responsável'}</h2>
                            <button className={styles.closeModalBtn} onClick={handleCloseModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className={styles.formContainer}>
                            <div className={styles.formContent}>

                                {/* Dados Pessoais */}
                                <h3 className={styles.sectionTitle}>Dados Pessoais</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Nome Completo *</label>
                                        <input type="text" name="nome" value={formData.nome} onChange={handleChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>E-mail *</label>
                                        <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Tipo Pessoa *</label>
                                        <select name="tipoPessoa" value={formData.tipoPessoa} onChange={handleChange} required>
                                            <option value={1}>Pessoa Física</option>
                                            <option value={2}>Pessoa Jurídica</option>
                                            <option value={3}>Estrangeiro</option>
                                        </select>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>CPF/CNPJ *</label>
                                        <input type="text" name="cpfCnpj" value={formData.cpfCnpj} onChange={handleChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>RG</label>
                                        <input type="text" name="rg" value={formData.rg} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Data de Nascimento</label>
                                        <input type="date" name="dataNascimento" value={formData.dataNascimento} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Nacionalidade</label>
                                        <input type="text" name="nacionalidade" value={formData.nacionalidade} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Estado Civil</label>
                                        <select name="estadoCivil" value={formData.estadoCivil} onChange={handleChange}>
                                            <option value="Solteiro(a)">Solteiro(a)</option>
                                            <option value="Casado(a)">Casado(a)</option>
                                            <option value="Divorciado(a)">Divorciado(a)</option>
                                            <option value="Viúvo(a)">Viúvo(a)</option>
                                            <option value="Separado(a)">Separado(a)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Dados Profissionais */}
                                <h3 className={styles.sectionTitle}>Dados Profissionais</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Grau de Instrução</label>
                                        <input type="text" name="grauInstrucao" value={formData.grauInstrucao} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Profissão</label>
                                        <input type="text" name="profissao" value={formData.profissao} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Local de Trabalho</label>
                                        <input type="text" name="localTrabalho" value={formData.localTrabalho} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Renda Mensal (R$)</label>
                                        <input type="number" step="0.01" name="rendaMensal" value={formData.rendaMensal} onChange={handleChange} />
                                    </div>
                                    <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                                        <label>
                                            <input type="checkbox" name="declaraIR" checked={formData.declaraIR} onChange={handleChange} />
                                            Declarante de Imposto de Renda
                                        </label>
                                    </div>
                                </div>

                                {/* Contatos */}
                                <h3 className={styles.sectionTitle}>Contatos</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Celular (Principal) *</label>
                                        <input type="text" name="telefone" value={formData.telefone} onChange={handleChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Tel. Residencial</label>
                                        <input type="text" name="telefoneResidencial" value={formData.telefoneResidencial} onChange={handleChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Tel. Comercial</label>
                                        <input type="text" name="telefoneComercial" value={formData.telefoneComercial} onChange={handleChange} />
                                    </div>
                                </div>

                                {/* Endereço */}
                                <h3 className={styles.sectionTitle}>Endereço</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>CEP *</label>
                                        <input type="text" name="cep" value={formData.endereco?.cep || ''} onChange={handleAddressChange} required />
                                    </div>
                                    <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                                        <label>Logradouro / Endereço *</label>
                                        <input type="text" name="endereco" value={formData.endereco?.endereco || ''} onChange={handleAddressChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Número</label>
                                        <input type="text" name="numero" value={formData.endereco?.numero || ''} onChange={handleAddressChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Complemento</label>
                                        <input type="text" name="complemento" value={formData.endereco?.complemento || ''} onChange={handleAddressChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Bairro</label>
                                        <input type="text" name="bairro" value={formData.endereco?.bairro || ''} onChange={handleAddressChange} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Cidade *</label>
                                        <input type="text" name="cidade" value={formData.endereco?.cidade || ''} onChange={handleAddressChange} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Estado (UF) *</label>
                                        <input type="text" name="estado" value={formData.endereco?.estado || ''} onChange={handleAddressChange} maxLength={2} required />
                                    </div>
                                </div>

                                {/* Alunos do Responsável */}
                                {editingId && (
                                    <div className={styles.alunosSection}>
                                        <AlunosByResponsavel
                                            responsavelId={editingId}
                                            responsavelNome={formData.nome}
                                        />
                                    </div>
                                )}

                            </div>

                            <div className={styles.modalFooter}>
                                <button type="button" onClick={handleCloseModal} className={styles.cancelButton}>Cancelar</button>
                                <button type="submit" className={styles.saveButton}>
                                    {editingId ? 'Salvar Alterações' : 'Cadastrar Responsável'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default ResponsaveisPage;
